// content/zones/zone2.ts — THE DROWNED ROOTS' zone mechanic.
//
//   DESIGN §5 Zone 2: "the floor is a rhythm — water slows you (not them), and
//   the flood comes on a timer you must read."
//
// Three programs live here, and between them they are the zone:
//
//   1. THE WATER. 'W' tiles cost the Warden 30% of their speed. Nothing else in
//      the game is slowed by them — the roster is amphibious on purpose, and
//      that asymmetry is the whole hazard (RESEARCH Q5).
//   2. THE SURGE. Every ~25s of room time the water floods: 3s of klaxon,
//      shimmer and a rising water line, then 4s of 15 dmg/s and a current that
//      shoves you toward the room's low side. Raised stone ('R') and dry floor
//      ('.') are safe. The beat is fixed and readable — this is a timing test,
//      not a coin flip.
//   3. THE ROSTER'S BRAINS. Mireskippers leap, Shellbacks present a shell you
//      have to get around, Tidecallers reel you back into the water the surge
//      is about to take. Registered here through game/enemies.ts's custom-brain
//      seam so no shared file learns anything about this zone.
//
// Every literal comes from data/zones/zone2.ts. The numbers written in this file
// are drawing geometry and nothing else.

import type { Draw, Enemy, PlayerState, Room, RunCtx, Vec, ZoneMechanic } from '../../types'
import {
  ZONE2,
  ZONE2_BOSS_ARENA,
  ZONE2_COLORS,
  ZONE2_ENEMIES,
  ZONE2_GENERATORS,
  ZONE2_ROOM_CURRENT,
  ZONE2_WATER,
} from '../../data/zones/zone2'
import { registerZone, registerZoneContent } from '../../game/zones'
import { moveEnemy, pursue, registerEnemyBrain, roleBrain } from '../../game/enemies'
import { PixelArt, registerPainter, registerRecipe, shade } from '../../engine/sprites'
import { emit } from '../../engine/particles'
import { LOGICAL_H, LOGICAL_W } from '../../engine/render'
import { TILE } from '../../game/world'

const C = ZONE2_COLORS
const W = ZONE2_WATER
const SURGE = ZONE2_WATER.surge
const DEG = Math.PI / 180

// ===========================================================================
// 1. THE WATER — the Warden's speed, and the one interface wrinkle in the zone
// ===========================================================================

/**
 * The water slow rides `PlayerState.zoneSpeedMul` — the zone layer the run
 * resets to 1 each step before mechanic.update, re-asserted here per frame.
 * (The worker's original accessor workaround was retired by the orchestrator
 * when the shared seam landed — see DECISIONS D-020.)
 */

/**
 * RunCtx does not carry `relicFlags` (types.ts is frozen), but game/relics.ts
 * documents it as "the contract with zones 2-6" and writes plain numbers to it.
 * Read structurally: a run without the field simply has no relics set.
 */
interface RelicFlagged {
  relicFlags?: Record<string, number>
}
const NO_FLAGS: Record<string, number> = {}
function relicFlags(ctx: RunCtx): Record<string, number> {
  return (ctx as unknown as RelicFlagged).relicFlags ?? NO_FLAGS
}

// ===========================================================================
// room geometry cache — built once per room, never per frame
// ===========================================================================

const T_OTHER = 0
const T_WATER = 1
const T_STONE = 2

let gw = 0
let gh = 0
let tiles = new Uint8Array(0)
/** World centres of every 'W' tile in the room — the Tidecallers' target set. */
let waterTiles: Vec[] = []
let floorLayer: HTMLCanvasElement | null = null
const current: Vec = { x: 0, y: 1 }

function tileKind(tx: number, ty: number): number {
  if (tx < 0 || ty < 0 || tx >= gw || ty >= gh) return T_OTHER
  return tiles[ty * gw + tx]
}

function isWaterAt(x: number, y: number): boolean {
  return tileKind(Math.floor(x / TILE), Math.floor(y / TILE)) === T_WATER
}

// ===========================================================================
// ripples — a tiny fixed pool the brains and the flood both draw into
// ===========================================================================

interface Ripple {
  x: number
  y: number
  r0: number
  r1: number
  t: number
  life: number
  color: string
}
const ripples: Ripple[] = []
for (let i = 0; i < 24; i++) ripples.push({ x: 0, y: 0, r0: 0, r1: 0, t: 0, life: 1, color: C.caustic })
let rippleHead = 0

function pushRipple(x: number, y: number, r0: number, r1: number, life: number, color: string): void {
  const r = ripples[rippleHead]
  rippleHead = (rippleHead + 1) % ripples.length
  r.x = x
  r.y = y
  r.r0 = r0
  r.r1 = r1
  r.t = life
  r.life = life
  r.color = color
}

/** A body entering or leaving the water throws a wake; dry ground throws grit. */
function splash(x: number, y: number, count: number): void {
  if (isWaterAt(x, y)) {
    emit(x, y, {
      count,
      color: C.caustic,
      speed: 130,
      speedVar: 90,
      life: 0.34,
      lifeVar: 0.16,
      size: 3,
      grav: 260,
      shape: 1,
    })
    pushRipple(x, y, 4, 26, 0.45, C.caustic)
  } else {
    emit(x, y, { count: Math.max(3, count >> 1), color: C.stone, speed: 90, life: 0.24, size: 2 })
  }
}

// ===========================================================================
// 2. THE ROSTER'S BRAINS
// ===========================================================================

function num(ai: Record<string, unknown>, key: string, d: number): number {
  const v = ai[key]
  return typeof v === 'number' ? v : d
}

/** Shortest signed angle from a to b. */
function angleDelta(a: number, b: number): number {
  let x = b - a
  while (x > Math.PI) x -= Math.PI * 2
  while (x < -Math.PI) x += Math.PI * 2
  return x
}

const brainDir: Vec = { x: 0, y: 0 }

// --- Mireskipper: the stock swarm brain, wrapped so the leap throws a wake ----
// DESIGN calls for frog-leapers whose lunge reads as an arc jump. The behaviour
// is the shared swarm archetype with tuned lunge numbers (data/zones/zone2.ts);
// the wrapper only adds the water reading — a takeoff splash, a landing ring —
// so a pack crossing a basin looks like frogs and not like rats.
const swarmBrain = roleBrain('swarm')

registerEnemyBrain('z2_mireskipper', (run, e, dt, speedMul) => {
  const wasAir = e.tags.z2air === true
  swarmBrain(run, e, dt, speedMul)
  // e.state === 2 is the shared brain's S_ATTACK — the committed lunge.
  const isAir = e.state === 2
  if (isAir !== wasAir) {
    e.tags.z2air = isAir
    splash(e.pos.x, e.pos.y, isAir ? 7 : 11)
    if (!isAir) run.sfx('hit')
  }
})

// --- Shellback: a shell you have to get around ------------------------------
//
// FLAGGED INTERPRETATION — directional immunity without touching damageEnemy.
//
// DESIGN: "armored snapping crawlers, frontal shell: flank or wait for
// lunge-miss." The shared damage path (game/game.ts damageEnemy) knows exactly
// one immunity switch, `tags.invulnerable`, and it is all-or-nothing; content
// cannot intercept a hit to ask where it came from.
//
// So the Shellback computes the answer itself, every step: it turns its shell
// toward the Warden at a limited rate, and holds `tags.invulnerable` true only
// while the Warden is inside that frontal arc. Step outside the arc — behind it,
// or beside it — and the tag drops on the same frame, so the very next blow
// lands. The turn rate is deliberately slower than a Warden circling at walking
// speed, which makes flanking a real, learnable input rather than a stat check.
//
// Both counterplays DESIGN names therefore exist, and the shell is genuinely
// directional. The honest limitation: the arc is measured against the *Warden's
// current position*, not against the origin of the particular projectile. For
// melee, the shield shove and point-blank shots those are the same thing; for an
// arrow fired from behind and then walked around, they are not. That edge is the
// entire cost of not being able to edit shared code, and it is worth naming.
//
// On top of the arc sits the temporal window: during the windup, the snap and
// the 1.2s recovery the shell is committed forward and the tag is off outright,
// so "wait for the lunge-miss" pays even to a player who never flanks. That is
// also what keeps a room from being unclearable by a stubborn bot.

const S_APPROACH = 0
const S_WINDUP = 1
const S_SNAP = 2
const S_RECOVER = 3

registerEnemyBrain('z2_shellback', (run, e, dt, speedMul) => {
  const ai = e.def.ai as Record<string, unknown>
  const halfArc = (num(ai, 'frontalArcDeg', 130) * DEG) / 2
  const turnRate = num(ai, 'turnRate', 2)
  const lungeRange = num(ai, 'lungeRange', 112)
  const lungeCd = num(ai, 'lungeCooldown', 3)
  const windup = num(ai, 'windup', 0.55)
  const lungeSpeed = num(ai, 'lungeSpeed', 470)
  const lungeTime = num(ai, 'lungeTime', 0.3)
  const snapDamage = num(ai, 'snapDamage', 1.6)
  const recover = num(ai, 'recover', 1.2)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1
  const toPlayer = Math.atan2(dy, dx)

  // the shell's heading, turned at a bounded rate and locked once committed
  let face = typeof e.tags.z2face === 'number' ? e.tags.z2face : toPlayer
  if (e.state === S_APPROACH || e.state === S_RECOVER) {
    const d = angleDelta(face, toPlayer)
    const step = turnRate * dt * (e.state === S_RECOVER ? 0.35 : 1)
    face += Math.max(-step, Math.min(step, d))
  }
  e.tags.z2face = face
  if (Math.abs(Math.cos(face)) > 0.2) e.facing = Math.cos(face) < 0 ? -1 : 1

  const covered = Math.abs(angleDelta(face, toPlayer)) <= halfArc
  const blocking = e.state === S_APPROACH && covered && e.spawnGrace <= 0
  e.tags.invulnerable = blocking
  e.tags.z2block = blocking

  if (e.state === S_WINDUP) {
    e.windup -= dt
    // braces: settles back onto its legs, shell up. No ground covered.
    moveEnemy(run, e, -Math.cos(face) * 26, -Math.sin(face) * 26, dt)
    if (e.windup <= 0) {
      e.state = S_SNAP
      e.stateT = 0
      e.tags.z2hit = false
      e.kbx = Math.cos(face) * lungeSpeed
      e.kby = Math.sin(face) * lungeSpeed
      run.sfx('swing')
      splash(e.pos.x, e.pos.y, 8)
    }
    return
  }

  if (e.state === S_SNAP) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.tags.z2hit !== true && dist < e.radius + run.player.radius + 8) {
      e.tags.z2hit = true
      run.damagePlayer(e.touchDamage * snapDamage, e.def.id)
    }
    if (e.stateT > lungeTime) {
      e.state = S_RECOVER
      e.stateT = 0
      splash(e.pos.x, e.pos.y, 10)
    }
    return
  }

  if (e.state === S_RECOVER) {
    // shell open, legs splayed: it can barely move, and it cannot block
    moveEnemy(run, e, -Math.cos(face) * 18, -Math.sin(face) * 18, dt)
    if (e.stateT > recover) {
      e.state = S_APPROACH
      e.stateT = 0
      e.cool = lungeCd
    }
    return
  }

  // approach: crawls shell-first, which is slower than a straight walk
  pursue(run, e, brainDir)
  const sp = e.speed * speedMul * (covered ? 1 : 0.82)
  moveEnemy(run, e, brainDir.x * sp, brainDir.y * sp, dt)
  if (dist < lungeRange + run.player.radius && e.cool <= 0 && e.spawnGrace <= 0) {
    e.state = S_WINDUP
    e.stateT = 0
    e.windup = windup
    e.telegraph = windup
    run.sfx('telegraph')
  }
})

// --- Tidecaller: the hook-beam ----------------------------------------------
//
// FLAGGED INTERPRETATION — "the pull is dodge-cancelable."
//
// DESIGN wants the hook broken by a dodge. Content cannot see the dash directly
// through RunCtx, but the run's Player carries `iframes`, and the custom-brain
// seam hands brains the real `Run` — so the break condition is read off the
// thing a dash actually produces: if the Warden is inside invulnerability frames
// when a pulse would land, the hook shears and the remaining pulses are
// cancelled. Two consequences worth stating: a dash is the intended answer and
// works, and a post-hit grace window also breaks the hook (0.6s of i-frames from
// any source). The second is a small gift to a player who just got hit, and it
// is the same reading the whole game already uses for "you cannot be touched
// right now".
//
// The pull is also three discrete pulses 0.3s apart rather than one long drag,
// so simply walking out between pulses is a partial answer even without a dash.

const B_IDLE = 0
const B_CHANNEL = 1
const B_PULL = 2
const B_REST = 3

const hookScratch: Vec = { x: 0, y: 0 }

/** Where the hook wants you: the deep water on the room's low side of you. */
function hookTarget(px: number, py: number, ahead: number, out: Vec): boolean {
  const ax = px + current.x * ahead
  const ay = py + current.y * ahead
  let best = -1
  let bestD = Infinity
  for (let i = 0; i < waterTiles.length; i++) {
    const t = waterTiles[i]
    const dx = t.x - ax
    const dy = t.y - ay
    const d = dx * dx + dy * dy
    if (d < bestD) {
      bestD = d
      best = i
    }
  }
  if (best < 0) {
    out.x = current.x
    out.y = current.y
    return waterTiles.length > 0
  }
  const t = waterTiles[best]
  const dx = t.x - px
  const dy = t.y - py
  const l = Math.hypot(dx, dy)
  if (l < 36) {
    // already standing in the deep the hook was aiming for: keep dragging along
    // the current instead of pulling nowhere
    out.x = current.x
    out.y = current.y
    return true
  }
  out.x = dx / l
  out.y = dy / l
  return true
}

registerEnemyBrain('z2_tidecaller', (run, e, dt, speedMul) => {
  const ai = e.def.ai as Record<string, unknown>
  const keep = num(ai, 'keepRange', 250)
  const minRange = num(ai, 'minRange', 200)
  const hookRange = num(ai, 'hookRange', 430)
  const hookCd = num(ai, 'hookCooldown', 5)
  const channel = num(ai, 'channel', 1.5)
  const pulses = num(ai, 'pulses', 3)
  const pulseGap = num(ai, 'pulseGap', 0.3)
  const impulse = num(ai, 'pullImpulse', 360)
  const ahead = num(ai, 'dragAhead', 128)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1
  const los = run.world.lineOfSight(e.pos.x, e.pos.y, px, py)
  if (Math.abs(dx) > 6) e.facing = dx < 0 ? -1 : 1

  if (e.state === B_CHANNEL) {
    e.windup -= dt
    e.telegraph = Math.max(e.telegraph, 0.001)
    moveEnemy(run, e, 0, 0, dt)
    e.tags.z2beam = 1
    if (!los || dist > hookRange * 1.25) {
      e.state = B_REST
      e.stateT = 0
      e.tags.z2beam = 0
      e.cool = hookCd * 0.5
      return
    }
    if (e.windup <= 0) {
      e.state = B_PULL
      e.stateT = 0
      e.windup = 0
      e.tags.z2pulses = pulses
      e.tags.z2beam = 2
      run.sfx('potion')
    }
    return
  }

  if (e.state === B_PULL) {
    moveEnemy(run, e, 0, 0, dt)
    e.tags.z2beam = 2
    e.windup -= dt
    if (e.windup <= 0) {
      const left = typeof e.tags.z2pulses === 'number' ? e.tags.z2pulses : 0
      // the dodge: i-frames when the pulse lands shear the hook
      if (run.player.iframes > 0) {
        e.tags.z2pulses = 0
        e.tags.z2beam = 0
        e.state = B_REST
        e.stateT = 0
        e.cool = hookCd
        run.sfx('ui')
        emit(px, py, { count: 12, color: C.hook, speed: 200, life: 0.3, size: 3, shape: 1 })
        return
      }
      if (hookTarget(px, py, ahead, hookScratch)) {
        run.pushPlayer(hookScratch.x * impulse, hookScratch.y * impulse)
        pushRipple(px, py, 6, 40, 0.4, C.hook)
        emit(px, py, {
          count: 8,
          color: C.hook,
          speed: 150,
          life: 0.26,
          size: 3,
          shape: 1,
          angle: Math.atan2(hookScratch.y, hookScratch.x),
          spread: 1.1,
        })
        run.sfx('dash')
      }
      e.tags.z2pulses = left - 1
      e.windup = pulseGap
      if (left - 1 <= 0) {
        e.state = B_REST
        e.stateT = 0
        e.tags.z2beam = 0
        e.cool = hookCd
      }
    }
    return
  }

  if (e.state === B_REST) {
    e.tags.z2beam = 0
    pursue(run, e, brainDir)
    const sp = e.speed * 0.7 * speedMul
    moveEnemy(run, e, -brainDir.x * sp, -brainDir.y * sp, dt)
    if (e.stateT > 0.6) {
      e.state = B_IDLE
      e.stateT = 0
    }
    return
  }

  // idle: hold the band, out past the reach of anything the Warden swings
  e.tags.z2beam = 0
  const sp = e.speed * speedMul
  let mx = 0
  let my = 0
  if (dist < minRange) {
    mx = -(dx / dist)
    my = -(dy / dist)
  } else if (dist > keep || !los) {
    pursue(run, e, brainDir)
    mx = brainDir.x
    my = brainDir.y
  } else {
    const s = e.bob * 0.6 + e.pos.x * 0.01
    mx = (-dy / dist) * Math.sin(s)
    my = (dx / dist) * Math.sin(s)
  }
  moveEnemy(run, e, mx * sp, my * sp, dt)

  if (e.cool <= 0 && e.spawnGrace <= 0 && dist < hookRange && los && waterTiles.length > 0) {
    e.state = B_CHANNEL
    e.stateT = 0
    e.windup = channel
    e.telegraph = channel
    e.tags.z2beam = 1
    run.sfx('telegraph')
  }
})

// ===========================================================================
// 3. THE ARENA SEAM
// The Cistern King floods quadrants of his own arena; the wash belongs *under*
// the bodies standing in it, which is this file's render pass and not his. He
// writes here, this file draws. Both files are zone 2's.
// ===========================================================================

export interface ArenaQuadFx {
  /** 0 dry · 0<k<=1 telegraph progress · -1 flooded · -2 drowned for good */
  state: number
  x: number
  y: number
  w: number
  h: number
}

export const arenaFx = {
  active: false,
  quads: [] as ArenaQuadFx[],
  wave: { on: false, x: 0, y: 0, r: 0, thickness: 0, wind: 0 },
}

// ===========================================================================
// 4. THE MECHANIC
// ===========================================================================

type SurgePhase = 'calm' | 'telegraph' | 'flood'

const viewR = { x: 0, y: 0, w: 0, h: 0, zoom: 1 }
const probeA: Vec = { x: 0, y: 0 }
const probeB: Vec = { x: 100, y: 0 }
const textAt: Vec = { x: 0, y: 0 }

/** Derive the visible world rectangle from the Draw surface (no allocation). */
function viewRect(d: Draw): void {
  const a = d.worldToScreen(probeA)
  const ax = a.x
  const b = d.worldToScreen(probeB)
  const zoom = (b.x - ax) / 100 || 1
  viewR.zoom = zoom
  viewR.x = d.camera.x - TILE * 2
  viewR.y = d.camera.y - TILE * 2
  viewR.w = LOGICAL_W / zoom + TILE * 4
  viewR.h = LOGICAL_H / zoom + TILE * 4
}

class DrownedRoots implements ZoneMechanic {
  readonly zoneId = 2 as const

  private ctx: RunCtx | null = null
  private anim = 0

  // --- the surge clock
  private phase: SurgePhase = 'calm'
  private t = 0
  private surges = 0
  private dmgAcc = 0
  private pushAcc = 0
  private rumbleAcc = 0
  private toldTheTide = false
  private inArena = false

  // ------------------------------------------------------------- lifecycle

  onZoneStart(ctx: RunCtx): void {
    this.ctx = ctx
    this.anim = 0
    this.toldTheTide = false
    arenaFx.active = false
    arenaFx.quads.length = 0
    ctx.bark('The Drowned Roots. The floor here keeps its own time.')
  }

  onRoomStart(ctx: RunCtx, room: Room): void {
    this.ctx = ctx
    this.inArena = room.template.id === ZONE2_BOSS_ARENA.id
    this.buildRoom(ctx, room)
    this.phase = 'calm'
    this.t = SURGE.firstDelay
    this.dmgAcc = 0
    this.pushAcc = 0
    this.rumbleAcc = 0
    this.surges = 0
    ctx.player.zoneSpeedMul = 1
    if (this.inArena || room.isRespite) return
    const line = ROOM_BARKS[Math.min(ROOM_BARKS.length - 1, room.index)]
    if (line) ctx.bark(line)
  }

  onZoneEnd(_ctx: RunCtx): void {
    floorLayer = null
    waterTiles = []
    tiles = new Uint8Array(0)
    gw = 0
    gh = 0
    arenaFx.active = false
    arenaFx.quads.length = 0
    this.ctx = null
  }

  // ------------------------------------------------------------- room build
  // findGlyphTiles allocates, so it is called exactly twice per room and the
  // results are cached for the frame loop (per-frame allocation discipline).

  private buildRoom(ctx: RunCtx, room: Room): void {
    const map = room.template.map
    gh = map.length
    gw = map[0]?.length ?? 0
    if (tiles.length !== gw * gh) tiles = new Uint8Array(gw * gh)
    tiles.fill(T_OTHER)

    waterTiles = ctx.findGlyphTiles('W')
    for (const p of waterTiles) {
      const tx = Math.floor(p.x / TILE)
      const ty = Math.floor(p.y / TILE)
      if (tx >= 0 && ty >= 0 && tx < gw && ty < gh) tiles[ty * gw + tx] = T_WATER
    }
    const stone = ctx.findGlyphTiles('R')
    for (const p of stone) {
      const tx = Math.floor(p.x / TILE)
      const ty = Math.floor(p.y / TILE)
      if (tx >= 0 && ty >= 0 && tx < gw && ty < gh) tiles[ty * gw + tx] = T_STONE
    }

    const dir = ZONE2_ROOM_CURRENT[room.template.id] ?? { x: 0, y: 1 }
    const l = Math.hypot(dir.x, dir.y) || 1
    current.x = dir.x / l
    current.y = dir.y / l

    floorLayer = paintFloorLayer(ctx)
    for (const r of ripples) r.t = 0
  }

  // ---------------------------------------------------------------- update

  update(ctx: RunCtx, dt: number): void {
    this.ctx = ctx
    this.anim += dt

    for (let i = 0; i < ripples.length; i++) if (ripples[i].t > 0) ripples[i].t -= dt

    // --- the water tax. Recomputed from 1 every step; never accumulated.
    const flags = relicFlags(ctx)
    const immune = (flags.waterSpeedImmunity ?? 0) > 0
    const dryBonus = flags.drySpeedBonus ?? 1
    const wet = ctx.tileAt(ctx.player.pos) === 'W'
    if (wet) {
      ctx.player.zoneSpeedMul = immune ? 1 : this.phase === 'flood' ? W.floodSlowMul : W.slowMul
    } else {
      ctx.player.zoneSpeedMul = immune && dryBonus > 1 ? dryBonus : 1
    }

    if (this.inArena || ctx.room.isRespite) return
    this.updateSurge(ctx, dt, wet)
  }

  /**
   * The beat. calm -> telegraph (3s of klaxon, shimmer and a rising water line)
   * -> flood (4s of 15 dmg/s and a current) -> calm. Fixed period, so a player
   * who counts can plan a crossing; the vault in room 3 is exactly that test.
   */
  private updateSurge(ctx: RunCtx, dt: number, wet: boolean): void {
    this.t -= dt

    if (this.phase === 'calm') {
      if (this.t <= 0) {
        this.phase = 'telegraph'
        this.t = SURGE.telegraph
        this.surges++
        ctx.sfx('door')
        ctx.sfx('telegraph')
        ctx.shake(1.6, 0.5)
        if (!this.toldTheTide) {
          this.toldTheTide = true
          ctx.bark('The klaxon in the rock. Get off the low ground.')
        }
      }
      return
    }

    if (this.phase === 'telegraph') {
      this.rumbleAcc -= dt
      if (this.rumbleAcc <= 0) {
        this.rumbleAcc = 0.75
        ctx.sfx('telegraph')
        ctx.shake(1.1, 0.3)
      }
      if (this.t <= 0) {
        this.phase = 'flood'
        this.t = SURGE.duration
        this.dmgAcc = 0
        this.pushAcc = 0
        ctx.sfx('generator-die')
        ctx.shake(7, 0.6)
        ctx.hitstop(0.05)
        this.spray(ctx)
      }
      return
    }

    // flooding — a continuous rumble under the whole four seconds. fx.shake
    // takes the maximum rather than accumulating, so re-arming it every step
    // holds the tremble instead of stacking it.
    ctx.shake(SURGE.shake, 0.15)
    this.dmgAcc += dt
    this.pushAcc += dt
    if (wet) {
      if (this.dmgAcc >= SURGE.damageTickSeconds) {
        this.dmgAcc = 0
        ctx.damagePlayer(SURGE.damagePerSecond * SURGE.damageTickSeconds, 'flood')
      }
      if (this.pushAcc >= SURGE.pushTickSeconds) {
        this.pushAcc = 0
        ctx.pushPlayer(current.x * SURGE.currentPush, current.y * SURGE.currentPush)
        pushRipple(ctx.player.pos.x, ctx.player.pos.y, 8, 46, 0.4, C.floodHot)
      }
    } else {
      this.dmgAcc = SURGE.damageTickSeconds
      this.pushAcc = SURGE.pushTickSeconds
    }

    if (this.t <= 0) {
      this.phase = 'calm'
      this.t = Math.max(2, SURGE.period - SURGE.telegraph - SURGE.duration)
      ctx.sfx('door')
    }
  }

  /** A wall of spray off the water when the surge breaks. */
  private spray(ctx: RunCtx): void {
    const px = ctx.player.pos.x
    const py = ctx.player.pos.y
    for (let i = 0; i < 5; i++) {
      const t = waterTiles[(Math.floor(ctx.rng() * waterTiles.length) | 0) % Math.max(1, waterTiles.length)]
      if (!t) break
      if (Math.abs(t.x - px) > 640 || Math.abs(t.y - py) > 400) continue
      emit(t.x, t.y, {
        count: 10,
        color: C.floodHot,
        speed: 220,
        speedVar: 160,
        life: 0.5,
        lifeVar: 0.2,
        size: 4,
        grav: 300,
        shape: 1,
      })
    }
  }

  // ---------------------------------------------------------------- render

  /**
   * Under the bodies: the baked floor (water, raised stone, shorelines, root
   * light) plus everything animated about the tide. The baked half is one
   * drawImage; the animated half only touches tiles inside the view.
   */
  renderUnder(d: Draw): void {
    if (gw === 0) return
    const ctx = d.ctx
    viewRect(d)

    if (floorLayer) {
      const sx = Math.max(0, Math.floor(viewR.x))
      const sy = Math.max(0, Math.floor(viewR.y))
      const sw = Math.min(floorLayer.width - sx, Math.ceil(viewR.w))
      const sh = Math.min(floorLayer.height - sy, Math.ceil(viewR.h))
      if (sw > 0 && sh > 0) ctx.drawImage(floorLayer, sx, sy, sw, sh, sx, sy, sw, sh)
    }

    const tx0 = Math.max(0, Math.floor(viewR.x / TILE))
    const ty0 = Math.max(0, Math.floor(viewR.y / TILE))
    const tx1 = Math.min(gw - 1, Math.ceil((viewR.x + viewR.w) / TILE))
    const ty1 = Math.min(gh - 1, Math.ceil((viewR.y + viewR.h) / TILE))

    this.renderCaustics(ctx, tx0, ty0, tx1, ty1)
    if (!this.inArena) this.renderTide(ctx, tx0, ty0, tx1, ty1)
    if (arenaFx.active) this.renderArenaQuads(ctx)
    this.renderRipples(ctx)
  }

  /** Root-light dancing on the surface. Deterministic, so it never flickers. */
  private renderCaustics(ctx: CanvasRenderingContext2D, tx0: number, ty0: number, tx1: number, ty1: number): void {
    const t = this.anim
    ctx.save()
    for (let pass = 0; pass < 2; pass++) {
      ctx.globalAlpha = pass === 0 ? 0.3 : 0.14
      ctx.strokeStyle = C.caustic
      ctx.lineWidth = pass === 0 ? 1.5 : 4
      ctx.beginPath()
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          if (tiles[ty * gw + tx] !== T_WATER) continue
          if (((tx * 5 + ty * 3) & 1) !== 0) continue
          const x = tx * TILE + 16
          const y = ty * TILE + 16
          const p = Math.sin(t * 1.7 + tx * 0.55 + ty * 0.9)
          const q = Math.cos(t * 1.2 + tx * 0.31 - ty * 0.44)
          ctx.moveTo(x - 10 + p * 5, y + q * 6)
          ctx.lineTo(x + 10 + p * 5, y + q * 6 - 3)
        }
      }
      ctx.stroke()
    }
    ctx.restore()
  }

  /**
   * The flood clock, drawn on the floor itself: during the telegraph the water
   * visibly *rises* up each tile, so a player learns the beat by watching the
   * ground rather than by reading a number.
   */
  private renderTide(ctx: CanvasRenderingContext2D, tx0: number, ty0: number, tx1: number, ty1: number): void {
    if (this.phase === 'calm') return
    const t = this.anim
    const rising = this.phase === 'telegraph'
    const k = rising ? 1 - Math.max(0, this.t) / SURGE.telegraph : 1

    ctx.save()
    if (rising) {
      // the level line climbs the tile; the crest gets brighter as it goes
      const h = Math.round(6 + k * (TILE - 6))
      ctx.globalAlpha = 0.16 + 0.3 * k
      ctx.fillStyle = C.surge
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          if (tiles[ty * gw + tx] !== T_WATER) continue
          ctx.fillRect(tx * TILE, ty * TILE + (TILE - h), TILE, h)
        }
      }
      ctx.globalAlpha = 0.35 + 0.45 * k
      ctx.fillStyle = C.floodHot
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          if (tiles[ty * gw + tx] !== T_WATER) continue
          const wob = Math.sin(t * 9 + tx * 0.8 + ty * 0.5) * 1.5
          ctx.fillRect(tx * TILE, ty * TILE + (TILE - h) + wob, TILE, 2)
        }
      }
    } else {
      const pulse = 0.34 + 0.12 * Math.sin(t * 14)
      ctx.globalAlpha = pulse
      ctx.fillStyle = C.flood
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          if (tiles[ty * gw + tx] !== T_WATER) continue
          ctx.fillRect(tx * TILE, ty * TILE, TILE, TILE)
        }
      }
      // foam torn along the current so the shove direction is visible
      ctx.globalAlpha = 0.55
      ctx.strokeStyle = C.floodHot
      ctx.lineWidth = 2
      ctx.beginPath()
      const drift = (t * 260) % 64
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          if (tiles[ty * gw + tx] !== T_WATER) continue
          if (((tx * 3 + ty * 7) & 1) !== 0) continue
          const cx = tx * TILE + 16 + current.x * drift * 0.5
          const cy = ty * TILE + 16 + current.y * drift * 0.5
          ctx.moveTo(cx - current.x * 10, cy - current.y * 10)
          ctx.lineTo(cx + current.x * 10, cy + current.y * 10)
        }
      }
      ctx.stroke()

      // the hot rim (readability law: unblockable carries a red edge). Every
      // shoreline of live flood burns, so "this whole shape is damage" is one
      // glance, not a colour the player has to have been taught.
      ctx.globalAlpha = 0.55 + 0.35 * Math.sin(t * 16)
      ctx.fillStyle = C.floodEdge
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          if (tiles[ty * gw + tx] !== T_WATER) continue
          const x = tx * TILE
          const y = ty * TILE
          if (tileKind(tx, ty - 1) !== T_WATER) ctx.fillRect(x, y, TILE, 3)
          if (tileKind(tx, ty + 1) !== T_WATER) ctx.fillRect(x, y + TILE - 3, TILE, 3)
          if (tileKind(tx - 1, ty) !== T_WATER) ctx.fillRect(x, y, 3, TILE)
          if (tileKind(tx + 1, ty) !== T_WATER) ctx.fillRect(x + TILE - 3, y, 3, TILE)
        }
      }
    }
    ctx.restore()
  }

  /** The Cistern King's quadrant washes, drawn beneath the fight. */
  private renderArenaQuads(ctx: CanvasRenderingContext2D): void {
    const t = this.anim
    ctx.save()
    for (const q of arenaFx.quads) {
      if (q.state === 0) continue
      if (q.state > 0) {
        const k = Math.min(1, q.state)
        const h = Math.round(4 + k * (q.h - 4))
        ctx.globalAlpha = 0.18 + 0.32 * k
        ctx.fillStyle = C.surge
        ctx.fillRect(q.x, q.y + (q.h - h), q.w, h)
        ctx.globalAlpha = 0.5 + 0.4 * k
        ctx.fillStyle = C.floodHot
        ctx.fillRect(q.x, q.y + (q.h - h) + Math.sin(t * 10) * 2, q.w, 3)
      } else if (q.state <= -2) {
        // drowned for good: dead black water, a cold rim, no pulse. It is not a
        // hazard any more — it is simply not floor.
        ctx.globalAlpha = 0.82
        ctx.fillStyle = C.waterDeep
        ctx.fillRect(q.x, q.y, q.w, q.h)
        ctx.globalAlpha = 0.5
        ctx.strokeStyle = C.waterEdge
        ctx.lineWidth = 3
        ctx.strokeRect(q.x + 1.5, q.y + 1.5, q.w - 3, q.h - 3)
      } else {
        ctx.globalAlpha = 0.4 + 0.12 * Math.sin(t * 13)
        ctx.fillStyle = C.flood
        ctx.fillRect(q.x, q.y, q.w, q.h)
        ctx.globalAlpha = 0.6 + 0.35 * Math.sin(t * 16)
        ctx.fillStyle = C.floodEdge
        ctx.fillRect(q.x, q.y, q.w, 4)
        ctx.fillRect(q.x, q.y + q.h - 4, q.w, 4)
        ctx.fillRect(q.x, q.y, 4, q.h)
        ctx.fillRect(q.x + q.w - 4, q.y, 4, q.h)
      }
    }
    ctx.restore()
  }

  private renderRipples(ctx: CanvasRenderingContext2D): void {
    ctx.save()
    ctx.lineWidth = 2
    for (const r of ripples) {
      if (r.t <= 0) continue
      const k = 1 - r.t / r.life
      ctx.globalAlpha = 0.55 * (1 - k)
      ctx.strokeStyle = r.color
      ctx.beginPath()
      ctx.arc(r.x, r.y, r.r0 + (r.r1 - r.r0) * k, 0, Math.PI * 2)
      ctx.stroke()
    }
    ctx.restore()
  }

  /**
   * Over the bodies: the roster's tells (a Shellback's shell, a Tidecaller's
   * beam) and the surge's screen cue, which has to be impossible to miss.
   */
  renderOver(d: Draw): void {
    const ctx = d.ctx
    const run = this.ctx
    if (!run) return
    this.renderShells(ctx, run)
    this.renderHooks(ctx, run)
    if (!this.inArena && this.phase !== 'calm') this.renderSurgeCue(d)
  }

  private renderShells(ctx: CanvasRenderingContext2D, run: RunCtx): void {
    const list = run.enemies
    ctx.save()
    for (let i = 0; i < list.length; i++) {
      const e = list[i]
      if (!e.alive || e.def.id !== 'shellback') continue
      const face = typeof e.tags.z2face === 'number' ? e.tags.z2face : 0
      const half = (num(e.def.ai as Record<string, unknown>, 'frontalArcDeg', 130) * DEG) / 2
      const blocking = e.tags.z2block === true
      const r = 15 + (e.elite === 'bloated' ? 5 : 0)
      if (blocking) {
        ctx.globalAlpha = 0.85
        ctx.strokeStyle = '#cfe8c4'
        ctx.lineWidth = 5
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y, r, face - half, face + half)
        ctx.stroke()
        ctx.globalAlpha = 0.3
        ctx.strokeStyle = '#7fae86'
        ctx.lineWidth = 9
        ctx.stroke()
      } else {
        // shell open: a broken arc, and the soft body showing behind it
        ctx.globalAlpha = 0.75
        ctx.strokeStyle = '#ff8a6a'
        ctx.lineWidth = 3
        ctx.setLineDash([5, 5])
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y, r, face - half, face + half)
        ctx.stroke()
        ctx.setLineDash([])
      }
    }
    ctx.restore()
  }

  private renderHooks(ctx: CanvasRenderingContext2D, run: RunCtx): void {
    const px = run.player.pos.x
    const py = run.player.pos.y
    const list = run.enemies
    ctx.save()
    for (let i = 0; i < list.length; i++) {
      const e = list[i]
      if (!e.alive || e.def.id !== 'tidecaller') continue
      const mode = typeof e.tags.z2beam === 'number' ? e.tags.z2beam : 0
      if (mode === 0) continue
      const pulling = mode === 2
      ctx.globalAlpha = pulling ? 0.9 : 0.4 + 0.3 * Math.sin(this.anim * 16)
      ctx.strokeStyle = C.hook
      ctx.lineWidth = pulling ? 4 : 2
      if (!pulling) ctx.setLineDash([7, 6])
      ctx.beginPath()
      ctx.moveTo(e.pos.x, e.pos.y - 6)
      ctx.lineTo(px, py)
      ctx.stroke()
      ctx.setLineDash([])
      if (pulling) {
        // chevrons travelling up the line: the direction you are being reeled
        const dx = e.pos.x - px
        const dy = e.pos.y - py - 6
        const l = Math.hypot(dx, dy) || 1
        const a = Math.atan2(dy, dx)
        ctx.globalAlpha = 0.95
        ctx.lineWidth = 3
        for (let k = 0; k < 4; k++) {
          const f = ((this.anim * 1.6 + k * 0.25) % 1) * l
          const cx = px + (dx / l) * f
          const cy = py + (dy / l) * f
          ctx.beginPath()
          ctx.moveTo(cx - Math.cos(a - 0.6) * 7, cy - Math.sin(a - 0.6) * 7)
          ctx.lineTo(cx, cy)
          ctx.lineTo(cx - Math.cos(a + 0.6) * 7, cy - Math.sin(a + 0.6) * 7)
          ctx.stroke()
        }
      }
    }
    ctx.restore()
  }

  /**
   * The surge's screen cue. renderOver draws in world space, so the frame is
   * built from the derived view rectangle — four bands hugging the edge of what
   * the player can actually see, plus a count above the Warden. Between the
   * rumble, the rising water and this, the tide cannot be missed.
   */
  private renderSurgeCue(d: Draw): void {
    const ctx = d.ctx
    const run = this.ctx
    if (!run) return
    const rising = this.phase === 'telegraph'
    const k = rising ? 1 - Math.max(0, this.t) / SURGE.telegraph : 1
    const band = 120 / viewR.zoom
    const x0 = viewR.x + TILE * 2
    const y0 = viewR.y + TILE * 2
    const w = viewR.w - TILE * 4
    const h = viewR.h - TILE * 4
    const color = rising ? C.surge : C.floodEdge
    const alpha = rising ? 0.1 + 0.45 * k * (0.55 + 0.45 * Math.sin(this.anim * 12)) : 0.4 + 0.18 * Math.sin(this.anim * 18)

    ctx.save()
    ctx.globalAlpha = alpha
    const top = ctx.createLinearGradient(0, y0, 0, y0 + band)
    top.addColorStop(0, color)
    top.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = top
    ctx.fillRect(x0, y0, w, band)
    const bot = ctx.createLinearGradient(0, y0 + h, 0, y0 + h - band)
    bot.addColorStop(0, color)
    bot.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = bot
    ctx.fillRect(x0, y0 + h - band, w, band)
    const left = ctx.createLinearGradient(x0, 0, x0 + band, 0)
    left.addColorStop(0, color)
    left.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = left
    ctx.fillRect(x0, y0, band, h)
    const right = ctx.createLinearGradient(x0 + w, 0, x0 + w - band, 0)
    right.addColorStop(0, color)
    right.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = right
    ctx.fillRect(x0 + w - band, y0, band, h)
    ctx.restore()

    textAt.x = run.player.pos.x
    textAt.y = run.player.pos.y - 46
    if (rising) {
      d.text(`THE TIDE — ${Math.max(0, this.t).toFixed(1)}`, textAt, 13, C.floodHot)
    } else if (run.tileAt(run.player.pos) === 'W') {
      d.text('GET OUT OF THE WATER', textAt, 14, '#ff8a6a')
    }
  }

  // --------------------------------------------------------------- test seam
  /** Read/steer the surge clock. Used only by test/zone2.mjs. */
  debugState(): { phase: SurgePhase; t: number; surges: number; current: Vec; waterTiles: number; inArena: boolean } {
    return {
      phase: this.phase,
      t: this.t,
      surges: this.surges,
      current,
      waterTiles: waterTiles.length,
      inArena: this.inArena,
    }
  }

  debugForceSurge(): void {
    if (this.phase !== 'calm') return
    this.t = 0
  }

  debugCalm(): void {
    this.phase = 'calm'
    this.t = 1e9
  }
}

const ROOM_BARKS = [
  'Shallow water. It does not slow them.',
  'Two sources, and the tide between you and both.',
  'Three. One behind iron, and the vault sits across the low ground.',
]

export const zone2Mechanic = new DrownedRoots()

/**
 * TEST SEAM (test/zone2.mjs). Steering the surge clock from a spec is the only
 * way to assert a 25-second rhythm without waiting 25 seconds a dozen times.
 * Inert unless something calls it; nothing in the game does.
 */
export const zone2Debug = {
  state: (): ReturnType<DrownedRoots['debugState']> => zone2Mechanic.debugState(),
  forceSurge: (): void => zone2Mechanic.debugForceSurge(),
  calm: (): void => zone2Mechanic.debugCalm(),
  isWaterAt,
}

// ===========================================================================
// 5. THE FLOOR LAYER
// One canvas per room: water depth, raised stone with a real edge, shorelines,
// and the root-light that is this zone's only warm thing. Composited once at
// room build; the frame loop blits a slice of it and animates on top.
// ===========================================================================

function paintFloorLayer(ctx: RunCtx): HTMLCanvasElement | null {
  if (gw === 0 || gh === 0) return null
  const c = document.createElement('canvas')
  c.width = gw * TILE
  c.height = gh * TILE
  const g = c.getContext('2d')
  if (!g) return null
  g.imageSmoothingEnabled = false

  // --- water first: a dark body with depth banding away from the shore
  for (let ty = 0; ty < gh; ty++) {
    for (let tx = 0; tx < gw; tx++) {
      if (tiles[ty * gw + tx] !== T_WATER) continue
      const x = tx * TILE
      const y = ty * TILE
      let open = 0
      if (tileKind(tx - 1, ty) === T_WATER) open++
      if (tileKind(tx + 1, ty) === T_WATER) open++
      if (tileKind(tx, ty - 1) === T_WATER) open++
      if (tileKind(tx, ty + 1) === T_WATER) open++
      g.fillStyle = open >= 4 ? C.waterDeep : C.waterShallow
      g.fillRect(x, y, TILE, TILE)
      // still-water banding, deterministic so tiles align
      g.globalAlpha = 0.18
      g.fillStyle = C.waterEdge
      for (let i = 0; i < 2; i++) {
        const yy = y + 8 + i * 13 + ((tx * 7 + ty * 5) % 5)
        g.fillRect(x + ((tx * 3 + ty) % 6), yy, TILE - 8, 1)
      }
      g.globalAlpha = 1
    }
  }

  // --- shorelines: a pale rim wherever water meets anything that is not water
  g.globalAlpha = 0.55
  g.fillStyle = C.waterEdge
  for (let ty = 0; ty < gh; ty++) {
    for (let tx = 0; tx < gw; tx++) {
      if (tiles[ty * gw + tx] !== T_WATER) continue
      const x = tx * TILE
      const y = ty * TILE
      if (tileKind(tx, ty - 1) !== T_WATER) g.fillRect(x, y, TILE, 2)
      if (tileKind(tx, ty + 1) !== T_WATER) g.fillRect(x, y + TILE - 2, TILE, 2)
      if (tileKind(tx - 1, ty) !== T_WATER) g.fillRect(x, y, 2, TILE)
      if (tileKind(tx + 1, ty) !== T_WATER) g.fillRect(x + TILE - 2, y, 2, TILE)
    }
  }
  g.globalAlpha = 1

  // --- raised stone: a slab with a lit top edge and a shadow it casts forward
  for (let ty = 0; ty < gh; ty++) {
    for (let tx = 0; tx < gw; tx++) {
      if (tiles[ty * gw + tx] !== T_STONE) continue
      const x = tx * TILE
      const y = ty * TILE
      g.fillStyle = C.stone
      g.fillRect(x, y, TILE, TILE)
      g.fillStyle = shade(C.stone, -0.18)
      for (let i = 2; i < TILE; i += 9) g.fillRect(x + ((tx + ty) % 4), y + i, TILE - 4, 1)
      if (tileKind(tx, ty - 1) !== T_STONE) {
        g.fillStyle = C.stoneTop
        g.fillRect(x, y, TILE, 3)
        g.fillStyle = shade(C.stoneTop, 0.25)
        g.fillRect(x, y, TILE, 1)
      }
      if (tileKind(tx - 1, ty) !== T_STONE) {
        g.fillStyle = shade(C.stone, 0.18)
        g.fillRect(x, y, 2, TILE)
      }
      if (tileKind(tx, ty + 1) !== T_STONE) {
        g.fillStyle = C.stoneShadow
        g.fillRect(x, y + TILE - 3, TILE, 3)
        g.globalAlpha = 0.45
        g.fillRect(x, y + TILE, TILE, 5)
        g.globalAlpha = 1
      }
    }
  }

  // --- root-light: strands hanging off every south-facing wall face, plus the
  // glow they cast on the floor. Deterministic hash, so the room is stable.
  for (let ty = 0; ty < gh - 1; ty++) {
    for (let tx = 0; tx < gw; tx++) {
      if (!ctx.isWall(tx, ty) || ctx.isWall(tx, ty + 1)) continue
      const h = ((tx * 73856093) ^ (ty * 19349663)) >>> 0
      if (h % 100 >= 34) continue
      const x = tx * TILE + 6 + (h % 14)
      const y = (ty + 1) * TILE
      const len = 9 + (h % 17)
      g.strokeStyle = C.rootDim
      g.lineWidth = 3
      g.beginPath()
      g.moveTo(x, y - 4)
      g.quadraticCurveTo(x + ((h % 7) - 3), y + len * 0.6, x + ((h % 11) - 5), y + len)
      g.stroke()
      g.strokeStyle = C.root
      g.lineWidth = 1
      g.stroke()
      const glow = g.createRadialGradient(x, y + len, 1, x, y + len, 26)
      glow.addColorStop(0, 'rgba(62,226,180,0.30)')
      glow.addColorStop(1, 'rgba(62,226,180,0)')
      g.fillStyle = glow
      g.fillRect(x - 26, y + len - 26, 52, 52)
      g.fillStyle = '#c9fff0'
      g.fillRect(x + ((h % 11) - 5) - 1, y + len - 1, 2, 2)
    }
  }

  return c
}

// ===========================================================================
// 6. SPRITES
// One silhouette per role, readable at a glance and readable from each other:
// a low wide leaper, a squat armoured dome, a tall thin robe. Palette-driven,
// chunky, dark-outlined — the same art language zone 1 speaks.
// ===========================================================================

function pstr(p: Record<string, number | string>, k: string, d: string): string {
  const v = p[k]
  return typeof v === 'string' ? v : d
}

registerPainter('mireskipper', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const skin = pstr(p, 'skin', '#3f7a63')
  const dark = shade(skin, -0.42)
  const belly = pstr(p, 'belly', '#bfe8c9')
  const eye = pstr(p, 'eye', '#ffe066')

  // coiled hind legs, thrown wide — the "about to spring" read
  a.ellipse(cx - s * 0.34, s * 0.62, s * 0.14, s * 0.2, dark)
  a.ellipse(cx + s * 0.34, s * 0.62, s * 0.14, s * 0.2, dark)
  a.rect(Math.round(cx - s * 0.44), Math.round(s * 0.74), 5, 3, dark)
  a.rect(Math.round(cx + s * 0.26), Math.round(s * 0.74), 5, 3, dark)
  // front feet, splayed forward
  a.rect(Math.round(cx - s * 0.26), Math.round(s * 0.78), 4, 3, dark)
  a.rect(Math.round(cx + s * 0.14), Math.round(s * 0.78), 4, 3, dark)

  // low wide body
  a.ellipse(cx, s * 0.58, s * 0.3, s * 0.22, skin)
  a.ellipse(cx, s * 0.66, s * 0.2, s * 0.11, belly)
  // bioluminescent spots along the spine
  a.set(Math.round(cx - s * 0.14), Math.round(s * 0.5), '#7ffbd6')
  a.set(Math.round(cx + s * 0.12), Math.round(s * 0.53), '#7ffbd6')
  a.set(Math.round(cx), Math.round(s * 0.46), '#7ffbd6')

  // flat head, wide mouth
  a.ellipse(cx, s * 0.34, s * 0.26, s * 0.16, skin)
  a.hline(Math.round(cx - s * 0.18), Math.round(s * 0.4), Math.round(s * 0.36), dark)
  // bulging eyes ON TOP of the head — the frog read, and the readability anchor
  a.disc(cx - s * 0.17, s * 0.24, s * 0.09, skin)
  a.disc(cx + s * 0.17, s * 0.24, s * 0.09, skin)
  a.disc(cx - s * 0.17, s * 0.23, s * 0.055, '#14100f')
  a.disc(cx + s * 0.17, s * 0.23, s * 0.055, '#14100f')
  a.rect(Math.round(cx - s * 0.19), Math.round(s * 0.21), 2, 2, eye)
  a.rect(Math.round(cx + s * 0.15), Math.round(s * 0.21), 2, 2, eye)
})

registerPainter('shellback', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const shell = pstr(p, 'shell', '#4a5f52')
  const rim = pstr(p, 'rim', '#a7c9a0')
  const flesh = pstr(p, 'flesh', '#8b6f5a')
  const dark = shade(shell, -0.45)

  // legs first, low and crab-like
  for (let i = -2; i <= 2; i++) {
    if (i === 0) continue
    const x = Math.round(cx + i * s * 0.2)
    a.line(x, Math.round(s * 0.66), Math.round(x + i * 3), Math.round(s * 0.86), dark)
    a.set(Math.round(x + i * 3), Math.round(s * 0.87), dark)
  }
  // claws
  a.ellipse(cx - s * 0.4, s * 0.6, s * 0.09, s * 0.07, flesh)
  a.ellipse(cx + s * 0.4, s * 0.6, s * 0.09, s * 0.07, flesh)

  // the head, out front and low — this is the end you do NOT want
  a.ellipse(cx, s * 0.74, s * 0.15, s * 0.1, flesh)
  a.set(Math.round(cx - s * 0.07), Math.round(s * 0.74), '#ffd76a')
  a.set(Math.round(cx + s * 0.06), Math.round(s * 0.74), '#ffd76a')
  a.hline(Math.round(cx - s * 0.1), Math.round(s * 0.8), Math.round(s * 0.2), '#241c18')

  // the dome: a heavy plated shell, ridged, with a hard bright rim on the
  // leading edge so "the shell is that way" reads without any UI at all
  a.ellipse(cx, s * 0.47, s * 0.42, s * 0.33, dark)
  a.ellipse(cx, s * 0.45, s * 0.38, s * 0.3, shell)
  a.ellipse(cx, s * 0.38, s * 0.26, s * 0.18, shade(shell, 0.2))
  for (let i = -2; i <= 2; i++) {
    const x = Math.round(cx + i * s * 0.13)
    a.vline(x, Math.round(s * 0.22), Math.round(s * 0.44), shade(shell, i === 0 ? 0.35 : -0.25))
  }
  a.ellipse(cx, s * 0.24, s * 0.12, s * 0.05, rim)
  // the rim, thick along the front (south) face
  for (let i = -12; i <= 12; i++) {
    const ang = (i / 12) * 1.1 + Math.PI / 2
    a.set(Math.round(cx + Math.cos(ang) * s * 0.38), Math.round(s * 0.45 + Math.sin(ang) * s * 0.3), rim)
    a.set(Math.round(cx + Math.cos(ang) * s * 0.35), Math.round(s * 0.45 + Math.sin(ang) * s * 0.28), shade(rim, -0.3))
  }
})

registerPainter('tidecaller', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const robe = pstr(p, 'robe', '#1f4a58')
  const robeLo = shade(robe, -0.4)
  const trim = pstr(p, 'trim', '#5cf2cb')
  const hook = pstr(p, 'hook', '#9fe8ff')

  // tall trailing robe — the "back rank, kill me" silhouette
  for (let y = 0; y < Math.round(s * 0.58); y++) {
    const t = y / (s * 0.58)
    const w = Math.round(s * (0.08 + 0.3 * t))
    a.hline(Math.round(cx - w), Math.round(s * 0.36 + y), w * 2, y % 6 === 5 ? robeLo : robe)
  }
  a.ellipse(cx, s * 0.94, s * 0.38, s * 0.05, robeLo)
  // wet hem, dragging
  a.hline(Math.round(cx - s * 0.34), Math.round(s * 0.9), Math.round(s * 0.68), trim)

  // hood over nothing at all
  a.ellipse(cx, s * 0.24, s * 0.18, s * 0.2, robe)
  a.ellipse(cx, s * 0.27, s * 0.12, s * 0.13, '#050f14')
  a.disc(cx, s * 0.27, s * 0.045, trim)
  a.disc(cx, s * 0.27, s * 0.02, '#e8fffb')

  // the hook arm, raised and ready — the thing that will drag you under
  const wx = Math.round(cx + s * 0.32)
  a.vline(wx, Math.round(s * 0.34), Math.round(s * 0.3), robeLo)
  a.line(wx, Math.round(s * 0.32), Math.round(wx + 3), Math.round(s * 0.18), hook)
  a.line(Math.round(wx + 3), Math.round(s * 0.18), Math.round(wx - 2), Math.round(s * 0.12), hook)
  a.set(Math.round(wx - 3), Math.round(s * 0.11), '#e8fffb')
  // chain motes
  a.set(Math.round(wx - 6), Math.round(s * 0.2), trim)
  a.set(Math.round(wx - 9), Math.round(s * 0.26), trim)
  // trim band
  a.hline(Math.round(cx - s * 0.2), Math.round(s * 0.48), Math.round(s * 0.4), trim)
  a.hline(Math.round(cx - s * 0.16), Math.round(s * 0.49), Math.round(s * 0.32), shade(trim, -0.5))
})

// --- generators: three tiers, each carrying zone 1's violet source-rift -------

function sourceMaw(a: PixelArt, cx: number, cy: number, rx: number, ry: number): void {
  a.ellipse(cx, cy, rx, ry, '#150c22')
  a.ellipse(cx, cy + 1, rx * 0.68, ry * 0.66, '#5b2f92')
  a.ellipse(cx, cy + 1, rx * 0.36, ry * 0.34, '#a774e8')
}

registerPainter('gen_rootcluster', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const root = pstr(p, 'root', '#2f8f74')
  const rootLo = shade(root, -0.45)
  const glow = pstr(p, 'glow', '#5cf2cb')

  // a drowned stump wrapped in living root, erupting out of the water
  a.ellipse(cx, s * 0.86, s * 0.44, s * 0.1, '#0c2129')
  a.ellipse(cx, s * 0.72, s * 0.34, s * 0.18, rootLo)
  a.ellipse(cx, s * 0.68, s * 0.28, s * 0.14, root)
  for (let i = -3; i <= 3; i++) {
    if (i === 0) continue
    const x = Math.round(cx + i * s * 0.1)
    a.line(x, Math.round(s * 0.68), Math.round(x + i * 4), Math.round(s * 0.26 + Math.abs(i) * 4), root)
    a.set(Math.round(x + i * 4), Math.round(s * 0.25 + Math.abs(i) * 4), glow)
  }
  a.line(Math.round(cx - s * 0.4), Math.round(s * 0.8), Math.round(cx + s * 0.36), Math.round(s * 0.76), rootLo)
  a.ellipse(cx, s * 0.4, s * 0.14, s * 0.16, root)
  a.ellipse(cx, s * 0.38, s * 0.07, s * 0.09, glow)
  a.set(Math.round(cx), Math.round(s * 0.36), '#eafff9')
  sourceMaw(a, cx, s * 0.68, s * 0.15, s * 0.11)
})

registerPainter('gen_drownedwell', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const stone = pstr(p, 'stone', '#4c6a74')
  const lo = shade(stone, -0.4)
  const hi = shade(stone, 0.28)

  // a broken well-ring standing in the water, its shaft open
  a.ellipse(cx, s * 0.66, s * 0.44, s * 0.26, lo)
  a.ellipse(cx, s * 0.62, s * 0.38, s * 0.22, stone)
  a.ellipse(cx, s * 0.6, s * 0.27, s * 0.15, '#08161b')
  for (let i = 0; i < 10; i++) {
    const ang = (i / 10) * Math.PI * 2
    const x = Math.round(cx + Math.cos(ang) * s * 0.34)
    const y = Math.round(s * 0.62 + Math.sin(ang) * s * 0.19)
    a.rect(x - 2, y - 2, 4, 4, i % 3 === 0 ? hi : stone)
  }
  // the broken lip — one course of the ring caved in
  a.rect(Math.round(cx - s * 0.42), Math.round(s * 0.5), Math.round(s * 0.16), 5, lo)
  a.rect(Math.round(cx + s * 0.26), Math.round(s * 0.46), Math.round(s * 0.14), 4, hi)
  // a fallen beam across it
  a.line(Math.round(cx - s * 0.44), Math.round(s * 0.36), Math.round(cx + s * 0.4), Math.round(s * 0.46), '#3a2b20')
  sourceMaw(a, cx, s * 0.6, s * 0.19, s * 0.11)
})

registerPainter('gen_tideidol', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const stone = pstr(p, 'stone', '#3d5f6b')
  const lo = shade(stone, -0.42)
  const hi = shade(stone, 0.3)
  const eye = pstr(p, 'eye', '#5ce0ff')

  // a carved stele, half-sunk, watching
  a.ellipse(cx, s * 0.9, s * 0.4, s * 0.08, '#0c2129')
  a.rect(Math.round(cx - s * 0.26), Math.round(s * 0.3), Math.round(s * 0.52), Math.round(s * 0.6), stone)
  a.rect(Math.round(cx - s * 0.26), Math.round(s * 0.3), Math.round(s * 0.52), 3, hi)
  a.vline(Math.round(cx - s * 0.26), Math.round(s * 0.3), Math.round(s * 0.6), hi)
  a.vline(Math.round(cx + s * 0.24), Math.round(s * 0.3), Math.round(s * 0.6), lo)
  // crowned head
  a.ellipse(cx, s * 0.28, s * 0.28, s * 0.16, stone)
  a.rect(Math.round(cx - s * 0.3), Math.round(s * 0.16), 4, 8, lo)
  a.rect(Math.round(cx + s * 0.22), Math.round(s * 0.16), 4, 8, lo)
  a.rect(Math.round(cx - s * 0.04), Math.round(s * 0.1), 4, 9, lo)
  // one hollow eye, lit
  a.ellipse(cx, s * 0.3, s * 0.1, s * 0.07, '#04121a')
  a.ellipse(cx, s * 0.3, s * 0.055, s * 0.035, eye)
  a.set(Math.round(cx), Math.round(s * 0.29), '#e8fbff')
  // carved tide-lines
  for (let i = 0; i < 3; i++) {
    a.hline(Math.round(cx - s * 0.2), Math.round(s * (0.52 + i * 0.11)), Math.round(s * 0.4), lo)
  }
  sourceMaw(a, cx, s * 0.74, s * 0.12, s * 0.1)
})

// A drain grate — scenery in the Cistern King's arena, drawn by the boss.
registerPainter('z2_drain', (a: PixelArt, _p, _pal) => {
  const s = a.size
  const cx = s / 2
  a.disc(cx, s * 0.55, s * 0.44, '#0a1b21')
  a.disc(cx, s * 0.55, s * 0.38, '#243c44')
  a.disc(cx, s * 0.55, s * 0.3, '#050f13')
  for (let i = -2; i <= 2; i++) {
    a.vline(Math.round(cx + i * 5), Math.round(s * 0.28), Math.round(s * 0.54), '#3d626d')
  }
  a.ring(cx, s * 0.55, s * 0.4, '#5f8f9b')
})

registerRecipe('z2_drain', { key: 'z2_drain', size: 34 })

// ===========================================================================
// registry
// ===========================================================================

registerZoneContent({
  enemies: ZONE2_ENEMIES,
  generators: ZONE2_GENERATORS,
})
registerZone(ZONE2, zone2Mechanic)
